import React, { FC, useEffect, useState } from "react";
import { useLocale } from "../../contexts/localeContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { useFormikContext } from "formik";
import { BIO_THREAT_LEVEL_ELEVATED, BIO_THREAT_LEVEL_HIGH, BIO_THREAT_LEVEL_LOW, BIO_THREAT_LEVEL_OFF} from "../../constants/biometric-fields-names";
import Tabs from "../tabs/tabs";
import { CustomFieldBuilder } from "../../containers/utilities/CustomFieldBuilder";
import { DynamicFieldDescriptor } from "../../models/DynamicFieldDescriptor";
import { getPresetInputsForIdemiaThreatLevels } from "../../services/biometricPresetService";
import ActivityFrame from "../activity-frame/activity-frame";
import { getErrorMessage } from "../../services/utilities/ServiceError";
import { useDialog } from "../dialog/DialogContext";

interface ThreatLevelContentFormProps {
    presetId: string
}

export const ThreatLevelCard: FC<ThreatLevelContentFormProps> = ({ presetId }) => {
    const nameAccessor = 'name';
    const formik = useFormikContext();
    const [hasError, setHasError] = useState(false);
    const [selectedTab, setSelectedTab] = useState(0);
    const { translate } = useLocale();
    const initialTabs = [
        { label: translate.byKey(TranslationKeys.off) , badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.low), badge: 0, showInitial: false, isActive: true },
        { label: translate.byKey(TranslationKeys.elevated), badge: 0, showInitial: false, isActive: true },
        { label: translate.byKey(TranslationKeys.high), badge: 0, showInitial: false, isActive: true },
    ];
    const [tabs, setTabs] = useState<any[]>(initialTabs);
    const customFieldBuilder: CustomFieldBuilder = new CustomFieldBuilder(formik, translate);
    const [busy, setBusy] = useState(false);
    const [supportedFields, setSupportedFields] = useState<DynamicFieldDescriptor[]>([]);
    let threatLevelSysName = BIO_THREAT_LEVEL_OFF;
    const dialog = useDialog();

    useEffect(() => {
        formik.validateForm();
    }, [(formik.values as any).custom])

    useEffect(() => {
        const supportedFieldAccessors = supportedFields?.map(field => field.systemName) ?? new Array(0);

        if (formik.errors[nameAccessor]
            || (supportedFieldAccessors.length > 0 && Object.keys(formik.errors as any).length > 0)) {
            setHasError(true)
        }
        else {
            setHasError(false)
        }
    }, [formik.errors]);

    useEffect(() => {
        reloadPage();
    }, [selectedTab])

    const reloadPage = async () => {
        try {
            setBusy(true);
            const settingData: any = await getPresetInputsForIdemiaThreatLevels(presetId, threatLevelSysName, translate);
            
            if (!!presetId) {
                const currentSupportedFields = settingData.elementFields;
                setSupportedFields(currentSupportedFields);
            }
            else {
                setSupportedFields(new Array<any>(0));
            }
        }
        catch (error) {
            dialog.pushError({
                title: translate.byKey(TranslationKeys.error),
                message: getErrorMessage(error),
            });
        }
        finally {
            setBusy(false);
        }
    }

    const selectTab = (i: number): void => {
        setSelectedTab(i);
    };

    const createFields = () => {
        if (selectedTab == 0){
            threatLevelSysName = BIO_THREAT_LEVEL_OFF;
        }
        else if (selectedTab == 1)
        {
            threatLevelSysName = BIO_THREAT_LEVEL_LOW;
        }
        else if (selectedTab == 2) {
            threatLevelSysName = BIO_THREAT_LEVEL_ELEVATED;
        }
        else if (selectedTab == 3) {
            threatLevelSysName = BIO_THREAT_LEVEL_HIGH;
        }

        var arrayFields = [] as any[];
        supportedFields?.filter(field => {
            if (field.systemName.includes(threatLevelSysName)) {
                arrayFields.push(field);
            }
        });

        return arrayFields?.reduce((formFields, field) =>
            customFieldBuilder.buildFormField(formFields, field, false, false, undefined, true),
            new Array(0))
            .map(fieldBundle => <div className="flex flex-row pt-2">{fieldBundle.length === 1
                ? fieldBundle[0].span
                    ? fieldBundle[0].content
                    : <>{fieldBundle[0].content}
                        <div className="flex flex-col flex-1 pl-3"></div></>
                : fieldBundle.map(field => field.content)}</div>)
    }

    return  <ActivityFrame busy={busy} className={`${busy ? 'mt-5 w-full h-full' : ''} flex flex-col flex-grow bg-white`}>
        <Tabs tabs={tabs}
            onSelect={selectTab}
            selected={selectedTab}
            add={false} onAdd={() => { }}
            isFlat={true}
            setSelectedTab={setSelectedTab}
            className="w-full">
            {createFields()}
        </Tabs>
    </ActivityFrame>
}

export default ThreatLevelCard;
